/**
 * Scale W4 debug (D-TBD): one vertex format per attribute. three r186 keys a render pipeline by the geometry's
 * attribute names, item sizes, normalized flags and interleave strides (`RenderObject.getGeometryCacheKey`), not by
 * their component type. Two geometries drawn by the same shader whose `color` (the ECL_Mask) is u8 in one (gltfpack
 * GLBs) and u16 in the other (Blender kit exports) therefore share one pipeline, and its vertex layout (format and
 * arrayStride) is whichever was built first: the other reads its colours at the wrong stride and format. A non-indexed
 * draw past the buffer is a WebGPU validation error (the code-built Hub's edge-field mesh after a Reach kit station:
 * `Draw(4098)` against a u8 buffer laid out at u16's stride 8); an indexed one silently reads the wrong masks.
 *
 * So every normalized Uint8 vertex attribute is widened to normalized Uint16 at load (v × 257, which is v / 255
 * exactly: value-identical), and a given attribute name, item size and normalized flag has one array type across
 * models. Interleaved 8-bit attributes come out as their own buffer. Load time only, never per frame.
 */
import { BufferAttribute, type BufferGeometry, type InterleavedBufferAttribute, type Mesh, type Object3D } from 'three/webgpu'

type Attr = BufferAttribute | InterleavedBufferAttribute

/** True for an attribute three reads as unsigned 8-bit normalized (it gets widened). */
export function isNarrowNormalized(attr: Attr): boolean {
  return attr.normalized && attr.array instanceof Uint8Array
}

function widen(attr: Attr): BufferAttribute {
  const n = attr.count
  const k = attr.itemSize
  const out = new Uint16Array(n * k)
  if ('isInterleavedBufferAttribute' in attr && attr.isInterleavedBufferAttribute) {
    const src = attr.data.array as Uint8Array
    const stride = attr.data.stride
    for (let i = 0; i < n; i++) for (let c = 0; c < k; c++) out[i * k + c] = src[i * stride + attr.offset + c] * 257
  } else {
    const src = attr.array as Uint8Array
    for (let i = 0; i < n * k; i++) out[i] = src[i] * 257
  }
  const wide = new BufferAttribute(out, k, true)
  wide.name = attr.name
  return wide
}

/**
 * Widens every normalized Uint8 vertex attribute of every mesh under `root` to normalized Uint16, in place. An
 * attribute shared by several geometries is widened once and stays shared. Returns the vertex bytes added.
 */
export function widenNarrowAttributes(root: Object3D): number {
  const done = new Map<Attr, BufferAttribute>()
  const geometries = new Set<BufferGeometry>()
  root.traverse((o) => {
    if ((o as Mesh).isMesh) geometries.add((o as Mesh).geometry)
  })
  let added = 0
  for (const g of geometries) {
    for (const name of Object.keys(g.attributes)) {
      const attr = g.attributes[name] as Attr
      if (!isNarrowNormalized(attr)) continue
      let wide = done.get(attr)
      if (!wide) {
        wide = widen(attr)
        done.set(attr, wide)
        added += attr.count * attr.itemSize
      }
      g.setAttribute(name, wide)
    }
  }
  return added
}
